import type {
	FetchFunction,
	GraphQLResponse,
	RequestParameters,
	Subscription,
} from 'relay-runtime';
import { Observable } from 'relay-runtime';
import delay from 'lodash/delay';

import { BadStatusError } from '@confluence/network';

import { calculateExponentialBackoff } from './calculateExponentialBackoff.ts';

const BAD_STATUS_CODE_MAX_RETRIES = 2;
const BAD_STATUS_CODE_INITIAL_RETRY_DELAY_MS = 500;
const BAD_STATUS_CODE_MAX_RETRY_DELAY_MS = 5000;

let retryEnabled = false;

const shouldRetryOnBadStatus = (error: Error, request: RequestParameters): boolean => {
	// Never retry if feature-flag not enabled
	if (!retryEnabled) return false;

	// If we didn't receive a 2xx status code, then...
	if (error instanceof BadStatusError) {
		// Don't retry mutations, regardless of all other concerns
		if (request.operationKind === 'mutation') return false;

		// When we've decided policy on which 5xx errors merit a retry, implement here.
		//
		// Nothing is retried until then, mirroring StatusCodeRetryLink, whose only `return true` (for
		// 504) is likewise commented out. This middleware previously returned true for every
		// `BadStatusError`, which meant it also retried 4xx: 401 (needs re-auth), 400/404
		// (deterministic), and — since `RateLimitedError extends BadStatusError` — 403 and 429, both of
		// which `networkStatusMiddleware` already surfaces as a global flag telling the user to refresh
		// or wait. Retrying underneath that contradicts the advice the product gives.
		//
		// The retry machinery below is deliberately kept so implementing a policy is a one-line change
		// here, and so it stays covered by tests. Decide the policy for both transports at once.
	}

	// All other errors, leave it to a higher middleware in the chain to retry if it wants to
	return false;
};

/**
 * Relay counterpart to Apollo's StatusCodeRetryLink.
 *
 * Retries queries (not mutations) on `BadStatusError` with exponential backoff — except that, like its
 * Apollo counterpart, no status code is currently eligible. See `shouldRetryOnBadStatus`.
 */
export const createStatusCodeRetryMiddleware = (fetchFn: FetchFunction): FetchFunction => {
	return (request, variables, cacheConfig) => {
		return Observable.create((observer) => {
			let retryCount = 0;
			let currentSubscription: Subscription | null = null;

			const attemptFetch = () => {
				currentSubscription = Observable.from(fetchFn(request, variables, cacheConfig)).subscribe({
					next(result: GraphQLResponse) {
						observer.next(result);
					},

					error(error: Error) {
						if (!shouldRetryOnBadStatus(error, request)) {
							observer.error(error);
							return;
						}

						// Increment before comparing, matching `apollo-link-retry`'s retry function:
						//
						//     return function retryFunction(count, operation, error) {
						//       if (count >= max) return false;
						//       ...
						//
						// where `count` is the already-incremented retry number. So the constant bounds total
						// attempts, not retries on top of the first attempt: 2 means one retry, exactly as
						// `NETWORK_ERROR_MAX_RETRIES` does in batchFetcher. Comparing before incrementing
						// yielded 3 attempts here while Apollo made 2.
						retryCount++;
						if (retryCount >= BAD_STATUS_CODE_MAX_RETRIES) {
							observer.error(error);
							return;
						}

						const retryDelay = calculateExponentialBackoff(
							retryCount,
							BAD_STATUS_CODE_INITIAL_RETRY_DELAY_MS,
							BAD_STATUS_CODE_MAX_RETRY_DELAY_MS,
						);
						if (process.env.NODE_ENV !== 'testing') {
							delay(() => attemptFetch(), retryDelay);
						} else {
							attemptFetch();
						}
					},
					complete() {
						observer.complete();
					},
				});
			};

			attemptFetch();

			return () => {
				if (currentSubscription) {
					currentSubscription.unsubscribe();
				}
			};
		});
	};
};

export function enableStatusCodeRetryMiddleware(): void {
	retryEnabled = true;
}

export function disableStatusCodeRetryMiddleware(): void {
	retryEnabled = false;
}

export function isStatusCodeRetryEnabled(): boolean {
	return retryEnabled;
}
